import React, { useCallback, useMemo, useState } from 'react';
import KpiStrip from './KpiStrip';
import InsightsSection from './InsightsSection';
import TrendChart from './TrendChart';
import Heatmap from './Heatmap';
import RepeatedPatterns from './RepeatedPatterns';
import TimeDistributionCard from './TimeDistributionCard';
import WeekdayDistributionCard from './WeekdayDistributionCard';
import DistributionDonutsCard from './DistributionDonutsCard';
import RankingsSection from './RankingsSection';
import ControlStateCard from './ControlStateCard';
import BusinessEventsDrawer from './BusinessEventsDrawer';
import { filterInsights } from '../summaryFormatters';
import { vocabForKind } from '../summaryVocab';

/**
 * Порядок блоков — ТЗ №1 §7.1. Один компонент для вкладок «Нарушения»
 * (kind='violation') и «Бизнес-метрики» (kind='business', BM-FE-TZ-002B/002C):
 * числа, KPI, графики и таблицы одинаковые, отличаются подписи. На бизнес-
 * вкладке нет «На что обратить внимание» и «Позитивных сигналов» — у бизнес-
 * событий нет оценки «хорошо/плохо»; повторяющиеся паттерны остаются
 * отдельным блоком, drill-down из них ведёт в бизнес-события.
 */
const OverviewBlocks = ({ kind = 'violation', data, scope, period, tables, tablesDisabled }) => {
  const isBusiness = kind === 'business';
  const vocab = vocabForKind(kind);
  const [businessDrilldown, setBusinessDrilldown] = useState(null);
  const closeBusinessEvents = useCallback(() => setBusinessDrilldown(null), []);
  const cameraNames = useMemo(
    () => Object.fromEntries(
      (data.control_continuity?.cameras || []).map((camera) => [camera.camera_id, camera.camera_name])
    ),
    [data.control_continuity]
  );
  const noViolations = data.kpis?.violations_count === 0;
  const comparisonState = data.scope?.comparison_state || 'available';
  const attention = useMemo(
    () => filterInsights(data.attention, comparisonState),
    [data.attention, comparisonState]
  );
  const patterns = Array.isArray(data.repeated_patterns) ? data.repeated_patterns : [];
  const positive = useMemo(
    () => filterInsights(data.positive_signals, comparisonState),
    [data.positive_signals, comparisonState]
  );

  const repeated = (
    <RepeatedPatterns
      patterns={patterns}
      cameras={data.control_continuity?.cameras}
      timezoneMode={data.scope?.timezone_mode}
      period={period}
      kind={kind}
      onOpenBusinessEvents={setBusinessDrilldown}
    />
  );

  return (
    <>
      <KpiStrip
        kpis={data.kpis}
        controlContinuity={data.control_continuity}
        comparisonState={comparisonState}
        vocab={vocab}
      />
      {isBusiness ? (
        patterns.length > 0 && <section className="sum-card">{repeated}</section>
      ) : (
        <div className="sum-two-col">
          <InsightsSection
            title="На что обратить внимание"
            items={attention}
            severityKind="attention"
            extraCount={patterns.length}
            cameraNames={cameraNames}
            emptyText={noViolations ? 'За период нарушений нет' : 'Замечаний нет'}
          >
            {repeated}
          </InsightsSection>
          <InsightsSection
            title="Позитивные сигналы"
            items={positive}
            severityKind="positive"
            cameraNames={cameraNames}
            emptyText="Позитивных сигналов за период нет"
          />
        </div>
      )}
      <DistributionDonutsCard
        ruleDistribution={data.rule_distribution}
        metricDistribution={data.metric_distribution}
        violationTypes={data.violation_types}
        vocab={vocab}
      />
      <TrendChart trend={data.trend} trendTimezone={data.scope?.trend_timezone} vocab={vocab} />
      {/* Распределения — каждое на всю ширину: так дни недели крупнее, а
          периоды времени суток помещаются колонками рядом. */}
      <WeekdayDistributionCard
        items={data.weekday_distribution}
        timezoneMode={data.scope?.timezone_mode}
        vocab={vocab}
      />
      <TimeDistributionCard
        distribution={data.time_distribution}
        previousDistribution={data.previous_time_distribution}
        timezoneMode={data.scope?.timezone_mode}
        vocab={vocab}
      />
      <Heatmap cells={data.heatmap} vocab={vocab} />
      <RankingsSection
        rankings={data.rankings}
        scope={scope}
        tables={tables}
        disabled={tablesDisabled}
        vocab={vocab}
        trendTimezone={data.scope?.trend_timezone}
      />
      {data.control_continuity && (
        <ControlStateCard controlContinuity={data.control_continuity} period={period} />
      )}
      {businessDrilldown && (
        <BusinessEventsDrawer drilldown={businessDrilldown} onClose={closeBusinessEvents} />
      )}
    </>
  );
};

export default OverviewBlocks;
